<template>
  <div class="flex-grow flex flex-col w-full relative">
    <div class="flex-grow overflow-scroll h-0 text-vice-dark-grey">
      <!-- Participants list -->
      <div class="flex flex-col max-h-full w-full">
        <div class="px-4 mt-5">
          <span class="font-bold">
            Clinician{{ usersPresenceStore.cliniciansList > 1 ? 's' : '' }}
          </span>
        </div>

        <div class="p-4">
          <div
            class="flex items-center justify-between mb-3"
            v-for="clinician in usersPresenceStore.cliniciansList"
            :key="clinician.id"
          >
            <div class="flex gap-x-3 items-center">
              <ViceAvatar
                :name="clinician.name"
                :image="clinician.profile_photo_url"
                class="w-12 h-12 text-lg min-w-12"
              />
              <div>
                {{ clinician.name }}
              </div>
            </div>
          </div>
          <div
            v-if="!usersPresenceStore?.cliniciansList?.length"
            class="span px-4 py-4 text-gray-500 text-center"
          >
            Clinician left
          </div>
        </div>

        <div class="px-4">
          <span class="font-bold">Attendees ({{ usersPresenceStore.totalParticipants }})</span>
        </div>

        <div class="p-4">
          <div
            v-if="!usersPresenceStore?.participantsList?.length"
            class="span px-4 py-4 text-gray-500 text-center"
          >
            No attendees
          </div>
          <div
            v-for="latecomer in latecomersList"
            :key="latecomer.id"
            class="p-2 bg-vice-light-grey rounded-lg"
          >
            <div class="flex gap-x-3 items-center mb-1">
              <ViceAvatar
                :name="latecomer.name"
                :image="latecomer.profile_photo_url"
                class="w-12 h-12 text-lg min-w-12"
              />
              <span>{{ latecomer.name }}</span>
            </div>
            <div class="flex items-center gap-x-3 justify-between mb-1">
              <span class="text-sm">Requests access:</span>
              <span class="flex gap-x-3">
                <ViceButton
                  type="button"
                  @click="handleLateEntryRequest(latecomer, false)"
                  medium
                  rose
                >
                  DENY
                </ViceButton>
                <ViceButton
                  type="button"
                  @click="handleLateEntryRequest(latecomer, true)"
                  medium
                  dark
                >
                  ACCEPT
                </ViceButton>
              </span>
            </div>
          </div>

          <div
            class="flex items-center justify-between mb-3"
            v-for="participant in sortedParticipants"
            :key="participant.id"
          >
            <div class="flex gap-x-3 items-center">
              <ViceAvatar
                :name="participant.name"
                :image="participant.profile_photo_url"
                class="w-12 h-12 text-lg min-w-12"
              />
              <div>
                {{ participant.name }}
              </div>
              <RaisedHandIcon
                v-if="handRaised(participant.id) && sessionStore.hasLivePhase"
                @click.stop="sessionStore.isClinician ? lowerHand(participant.id) : null"
                :class="{ 'cursor-pointer': sessionStore.isClinician }"
              />
            </div>
            <ParticipantAVIndicator
              :userId="participant.id"
              :muteSingleParticipant="muteSingleParticipant"
              :kickParticipant="kickParticipant"
            />
          </div>
        </div>

        <div class="px-4">
          <span class="font-bold">Observers ({{ usersPresenceStore.totalObservers }})</span>
        </div>

        <div class="p-4">
          <div
            v-if="!usersPresenceStore.observersList.length"
            class="span px-4 text-gray-500 text-center"
          >
            No observers
          </div>
          <div
            class="flex items-center justify-between mb-3"
            v-for="participant in usersPresenceStore.observersList"
            :key="participant.id"
          >
            <div class="flex gap-x-3 items-center">
              <ViceAvatar
                :name="participant.name"
                :image="participant.profile_photo_url"
                class="w-12 h-12 text-lg min-w-12"
              />
              <div>
                {{ participant.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import RaisedHandIcon from '@/Components/Icons/RaisedHandIcon.vue';
import ParticipantAVIndicator from '@/Components/Session/Sidebar/ParticipantAVIndicator.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useUsersPresenceStore from '@/Stores/useUsersPresenceStore';

export default {
  components: {
    ViceButton,
    ViceAvatar,
    RaisedHandIcon,
    ParticipantAVIndicator,
  },
  emits: ['lateEntryResponse', 'lowerHand'],
  props: {
    muteSingleParticipant: {
      type: Function,
    },
    latecomersList: {
      type: Object,
    },
    kickParticipant: {
      type: Function,
    },
  },
  setup(props, { emit }) {
    const usersPresenceStore = useUsersPresenceStore();
    const sessionStore = useSessionStore();

    const handleLateEntryRequest = (user, accepted) => {
      emit('lateEntryResponse', user, accepted);
    };

    const sortedParticipants = computed(() => {
      const participants = usersPresenceStore.participantsList;
      return [...participants]
        .map((participant) => ({
          ...participant,
          handRaised: sessionStore.isHandRaised(participant.id),
          handRaisedTimestamp: sessionStore.getHandRaisedTimestamp(participant.id),
        }))
        .sort((a, b) => {
          if (a.handRaised && !b.handRaised) return -1;
          if (!a.handRaised && b.handRaised) return 1;
          if (a.handRaised && b.handRaised) {
            return a.handRaisedTimestamp - b.handRaisedTimestamp;
          }
          return 0;
        });
    });

    const handRaised = (participantId) => sessionStore.isHandRaised(participantId);
    const lowerHand = (participantId) =>
      emit('lowerHand', { state: false, userId: participantId, time: Date.now() });

    return {
      sessionStore,
      usersPresenceStore,
      handleLateEntryRequest,
      sortedParticipants,
      handRaised,
      lowerHand,
    };
  },
};
</script>

<style>
.min-w-12 {
  min-width: 3rem;
}
</style>
